import { useState } from 'react';
import type { AttachmentDownloader, AttachmentMeta, ChainEmail } from '../../types/portal';
import AttachmentList from './AttachmentList';
import ChainAttachmentRollup from './ChainAttachmentRollup';
import EmailHtmlViewer from './EmailHtmlViewer';
import { formatDateTime } from '../../utils/dateFormatters';
import { bareEmailAddress, senderAddressSuffix } from '../../utils/senderDisplay';
import styles from './EmailChainThread.module.css';

interface EmailChainThreadProps {
  emails: ChainEmail[];
  /** Auto-expand the head (newest) email */
  autoExpandHead?: boolean;
  /**
   * Override attachment byte-fetching (the Tech Support Co-Pilot reads its
   * attachments live from Gmail). Defaults to the orders-portal path.
   */
  downloadAttachment?: AttachmentDownloader;
  /**
   * Open a previewable attachment inline (beside the order) instead of in the
   * full-screen overlay; forwarded to every message's AttachmentList.
   */
  onOpenAttachmentInline?: (att: AttachmentMeta, emailId: string) => void;
}

/**
 * Renders a vertical timeline of emails in a chain.
 * Newest email (head) is shown at the top, expanded.
 * Older emails are collapsed with expand/collapse toggle.
 * Displayed in reverse-chronological order (newest first).
 *
 * Recorder (kanban c7dd6465, the PR #2821 / 1790c14f residual): every message
 * header is a <button> whose visible text is the sender's name, address, the
 * Latest / Sent badges and the date, so without a data-action the shared
 * recorder named a click "button_<first>_<last>_<local>_<domain>_oct…" and a
 * person's name and email address landed in ui_action_events.action. The
 * header now records email_chain_message_toggle with the message id in
 * data-target and open / close from aria-expanded; Expand all / Collapse all
 * are email_chain_expand_all / email_chain_collapse_all with the head id.
 * Nothing about the sender is recorded.
 */
const EmailChainThread: React.FC<EmailChainThreadProps> = ({
  emails,
  autoExpandHead = true,
  downloadAttachment,
  onOpenAttachmentInline,
}) => {
  // Track which emails are expanded (by id)
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (autoExpandHead && emails.length > 0) {
      const head = emails.find((e) => e.is_head);
      if (head) initial.add(head.id);
      else if (emails[0]) initial.add(emails[0].id);
    }
    return initial;
  });

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    setExpanded(new Set(emails.map((e) => e.id)));
  };

  const collapseAll = () => {
    setExpanded(new Set<string>());
  };

  if (emails.length === 0) {
    return <div className={styles.empty}>No emails in this chain.</div>;
  }

  // Gmail-style reading order: newest (head) first, expanded at the top;
  // older messages follow below, collapsed. The chain API already returns
  // newest-first, so render as-is.
  const newestFirst = emails;
  const headId = (emails.find((e) => e.is_head) ?? emails[0])?.id;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.count}>
          {emails.length} message{emails.length !== 1 ? 's' : ''} in chain
        </span>
        <div className={styles.headerActions}>
          <button
            className={styles.headerBtn}
            onClick={expandAll}
            type="button"
            data-action="email_chain_expand_all"
            data-target-email-id={headId}
          >
            Expand all
          </button>
          <button
            className={styles.headerBtn}
            onClick={collapseAll}
            type="button"
            data-action="email_chain_collapse_all"
            data-target-email-id={headId}
          >
            Collapse all
          </button>
        </div>
      </div>

      {/* Chain-level attachment rollup — shows every attachment across the
          whole thread at the top, with provenance. Hidden for single-message
          chains (per-email AttachmentList is sufficient). */}
      <ChainAttachmentRollup
        emails={emails}
        download={downloadAttachment}
        onOpenInline={onOpenAttachmentInline}
      />

      <div className={styles.timeline}>
        {newestFirst.map((email, idx) => {
          const isExpanded = expanded.has(email.id);
          const isHead = email.is_head;
          const isLast = idx === newestFirst.length - 1;
          // Always surface the real sender address — a friendly display name
          // alone can mask a spoofed sender (phishing triage).
          const senderAddr = senderAddressSuffix(email.from_name, email.from_address);
          const fromAddr = bareEmailAddress(email.from_address);
          const replyTo = bareEmailAddress(email.reply_to);
          const showReplyTo =
            !!replyTo && replyTo.toLowerCase() !== (fromAddr ?? '').toLowerCase();

          return (
            <div
              key={email.id}
              className={`${styles.emailCard} ${isHead ? styles.headCard : ''} ${isExpanded ? styles.expanded : ''}`}
            >
              {/* Timeline connector */}
              {!isLast && <div className={styles.connector} />}

              {/* Timeline dot */}
              <div className={`${styles.dot} ${isHead ? styles.dotHead : ''}`} />

              {/* Email header (always visible, clickable to expand/collapse) */}
              <button
                className={styles.emailHeader}
                onClick={() => toggleExpanded(email.id)}
                type="button"
                aria-expanded={isExpanded}
                data-action="email_chain_message_toggle"
                data-target-email-id={email.id}
                data-testid="email-chain-message-header"
              >
                <div className={styles.emailMeta}>
                  <span className={styles.emailFrom}>
                    {email.from_name}
                    {senderAddr && (
                      <span className={styles.emailFromAddress} title={senderAddr}>
                        {`<${senderAddr}>`}
                      </span>
                    )}
                    {isHead && <span className={styles.headBadge}>Latest</span>}
                    {email.is_sent_reply && <span className={styles.sentBadge}>Sent</span>}
                  </span>
                  <span className={styles.emailDate}>
                    {email.received_at
                      ? formatDateTime(email.received_at)
                      : 'Unknown date'}
                  </span>
                </div>
                {!isExpanded && email.subject && (
                  <div className={styles.emailSubjectPreview}>
                    {email.subject}
                  </div>
                )}
                <svg
                  className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Email body — always rendered for print; hidden on screen when collapsed */}
              <div className={`${styles.emailBody} ${!isExpanded ? styles.emailBodyCollapsed : ''}`}>
                <div className={styles.emailAddresses}>
                  {fromAddr && (
                    <span>From: {fromAddr}</span>
                  )}
                  {showReplyTo && (
                    <span>Reply-To: {replyTo}</span>
                  )}
                  {email.to_address && (
                    <span>To: {email.to_address}</span>
                  )}
                  {email.cc_addresses && (
                    <span>CC: {email.cc_addresses}</span>
                  )}
                </div>
                {email.body_html ? (
                  <EmailHtmlViewer html={email.body_html} />
                ) : (
                  <div className={styles.emailText}>
                    {email.body_text || '(No content)'}
                  </div>
                )}
                {email.attachments && email.attachments.length > 0 && (
                  <AttachmentList
                    attachments={email.attachments}
                    emailId={email.id}
                    download={downloadAttachment}
                    onOpenInline={onOpenAttachmentInline}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmailChainThread;
